/**
 * Organization Switcher Component
 *
 * Batman-only component that allows switching between organizations.
 * Triggers a full page reload to ensure all data is fresh for the new org.
 */

import { useState } from 'react';
import { Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

export function OrgSwitcher() {
  const { isBatman } = useAuth();
  const { organizations, currentOrganization } = useOrganization();

  // Track if this has ever been visible — once shown, never hide. Prevents the
  // switcher flickering out while isBatman resolves through the auth cascade.
  //
  // State adjusted during render, not a ref: a ref written during render is
  // mutated even by renders React discards (StrictMode double-invoke, an
  // interrupted transition), so the latch could set from a render that never
  // committed. React documents this state pattern for precisely this case.
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  const shouldShow = isBatman && organizations.length > 1;

  if (shouldShow && !hasBeenVisible) {
    setHasBeenVisible(true);
  }

  if (!shouldShow && !hasBeenVisible) {
    return null;
  }

  const handleOrgChange = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    if (org && org.id !== currentOrganization?.id) {
      // Update localStorage directly - don't trigger React state update
      // which would cause a re-render before navigation completes
      localStorage.setItem('adcanvas_current_org', orgId);
      // Full page reload to the new org URL
      window.location.href = `/${org.slug}`;
    }
  };

  return (
    <div className="px-2 py-2">
      <div className="flex items-center gap-2 px-2 mb-2 text-xs text-muted-foreground">
        <Building2 className="size-3" />
        <span>Switch Organization</span>
      </div>
      <Select
        value={currentOrganization?.id || ''}
        onValueChange={handleOrgChange}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select organization" />
        </SelectTrigger>
        <SelectContent>
          {organizations.map((org) => (
            <SelectItem key={org.id} value={org.id}>
              {org.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
